import React, { useEffect, useState } from 'react'
import {ExternalLink, DollarSign, CheckCircle, Clock, XCircle} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface TransactionHistoryProps {
  userId?: string
}

export function TransactionHistory({ userId }: TransactionHistoryProps) {
  const [transactions, setTransactions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (userId) {
      fetchTransactions()
    }
  }, [userId])

  const fetchTransactions = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.transactions.list({
        filter: {},
        sort: { createdAt: -1 },
        limit: 50
      })
      setTransactions(list)
    } catch (error) {
      console.error('Failed to fetch transactions:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'released':
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-emerald-400" />
      case 'escrowed':
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-400" />
      case 'disputed':
      case 'refunded':
        return <XCircle className="w-5 h-5 text-red-400" />
      default:
        return <DollarSign className="w-5 h-5 text-electric-purple-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'released':
      case 'completed':
        return 'bg-emerald-500/20 text-emerald-400'
      case 'escrowed':
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400'
      case 'disputed':
      case 'refunded':
        return 'bg-red-500/20 text-red-400'
      default:
        return 'bg-electric-purple-500/20 text-electric-purple-400'
    }
  }

  if (!userId) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-400 text-lg">Please login to view transactions</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-electric-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-white mb-2">Transaction History</h2>
        <p className="text-slate-400">Your payment activity on the platform</p>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Chain
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Currency
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Task ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  TX Hash
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {transactions.map((tx) => (
                <tr key={tx._id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(tx.status)}
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(tx.status)}`}>
                        {tx.status?.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-lg font-bold text-white">${tx.amount}</div>
                    {tx.platformFee && (
                      <div className="text-xs text-slate-500">Fee: ${tx.platformFee}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-electric-purple-400 font-medium">{tx.chain || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-slate-300 font-medium">{tx.currency || 'USDC'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-slate-400 text-sm font-mono">
                      {tx.taskId?.substring(0, 8)}...
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {tx.txHash ? (
                      <a
                        href={`https://etherscan.io/tx/${tx.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-electric-purple-400 hover:text-electric-purple-300 transition-colors"
                      >
                        <span className="font-mono text-sm">
                          {tx.txHash.substring(0, 10)}...
                        </span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="text-slate-600">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-slate-400 text-sm">
                    {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {transactions.length === 0 && (
        <div className="text-center py-20">
          <DollarSign className="w-16 h-16 text-slate-700 mx-auto mb-4" />
          <p className="text-slate-400 text-lg">No transactions yet</p>
        </div>
      )}
    </div>
  )
}